import * as React from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { answerRsvp, getRsvp } from "../../services/api/rsvp";

/** The email buttons say ?a=going or ?a=not-going; the server records "YES" or "NO". */
const ANSWER_FROM_LINK = { going: "YES", "not-going": "NO" };

/**
 * Where the "Going" / "Not going" buttons in reminder emails land. No login needed: the token in the link
 * identifies one player's registration for one game.
 *
 * Opening the link applies the answer straight away and the page confirms it. It is this page's script that
 * records the answer (not the link itself), and the answer is dropped from the address afterwards, so a
 * refresh or a mail scanner opening the link can't re-apply or invent one.
 */
export default function RsvpView() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = ANSWER_FROM_LINK[searchParams.get("a") ?? ""] ?? null;

  const [state, setState] = React.useState({ status: "loading", info: null, message: "" });
  const [busy, setBusy] = React.useState(false);
  const [changeError, setChangeError] = React.useState("");
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const info = requested ? await answerRsvp(token, requested) : await getRsvp(token);
        setState({ status: "ready", info, message: "" });
      } catch (err) {
        setState({ status: "error", info: null, message: err instanceof Error ? err.message : "Something went wrong." });
      }
      // Forget the answer in the address so reloading shows the current state instead of repeating it.
      if (requested) setSearchParams({}, { replace: true });
    })();
  }, [token, requested, setSearchParams]);

  const change = async (response) => {
    setBusy(true);
    setChangeError("");
    try {
      setState({ status: "ready", info: await answerRsvp(token, response), message: "" });
    } catch (err) {
      setChangeError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.5} alignItems="center" sx={{ textAlign: "center", py: 1 }}>
            {children}
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );

  if (state.status === "loading") {
    return shell(
      <>
        <CircularProgress aria-label="Updating your attendance" />
        <Typography>{requested ? "Updating your attendance..." : "Loading..."}</Typography>
      </>,
    );
  }

  if (state.status === "error") {
    return shell(
      <>
        <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
          {state.message}
        </Alert>
        <Button component={RouterLink} to="/my-schedule" variant="outlined">
          View my schedule
        </Button>
      </>,
    );
  }

  const { info } = state;
  const going = info.rsvp === "YES";
  const notGoing = info.rsvp === "NO";
  const open = info.state === "open";

  const gameDetails = (
    <Box>
      <Typography sx={{ fontWeight: 600 }}>{info.leagueName}</Typography>
      <Typography color="text.secondary">{info.whenText}</Typography>
      {info.venue ? <Typography color="text.secondary">{info.venue}</Typography> : null}
    </Box>
  );

  return shell(
    <>
      {going ? <CheckCircleIcon color="success" sx={{ fontSize: 64 }} aria-hidden /> : null}
      {notGoing ? <CancelIcon color="error" sx={{ fontSize: 64 }} aria-hidden /> : null}

      <Box role="status" aria-live="polite">
        {going ? (
          <>
            <Typography variant="h5" component="h1">
              You&apos;re going!
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Thanks, {info.firstName}. We&apos;ve marked you as going.
            </Typography>
          </>
        ) : notGoing ? (
          <>
            <Typography variant="h5" component="h1">
              Got it, you&apos;re not going
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Thanks for letting us know, {info.firstName}. We&apos;ve marked you as not going so the organizers can plan.
            </Typography>
          </>
        ) : (
          <Typography variant="h5" component="h1">
            Will you be there?
          </Typography>
        )}
      </Box>

      {gameDetails}

      {!open ? (
        <Alert severity="info" sx={{ width: "100%", textAlign: "left" }}>
          {info.state === "cancelled"
            ? "This game was cancelled, so there's nothing to RSVP to."
            : "This game has already started, so attendance can't be changed here."}
        </Alert>
      ) : (
        <Stack spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
          {going || notGoing ? <Typography variant="body2">Changed your mind?</Typography> : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%" }} justifyContent="center">
            {!going ? (
              <Button variant="contained" color="success" size="large" onClick={() => change("YES")} disabled={busy}>
                Going
              </Button>
            ) : null}
            {!notGoing ? (
              <Button variant="contained" color="error" size="large" onClick={() => change("NO")} disabled={busy}>
                Not going
              </Button>
            ) : null}
          </Stack>
          {changeError ? (
            <Alert severity="error" sx={{ width: "100%", textAlign: "left" }} onClose={() => setChangeError("")}>
              {changeError}
            </Alert>
          ) : null}
        </Stack>
      )}

      <Button component={RouterLink} to="/my-schedule" color="inherit">
        View my schedule
      </Button>
    </>,
  );
}
